import React, { useState, useRef, useEffect } from 'react';
import { FiUpload, FiFile, FiX, FiFileText, FiCheckCircle, FiLoader } from 'react-icons/fi';
import { BsFiletypePdf, BsFiletypeDoc, BsFiletypeDocx, BsFiletypeTxt } from 'react-icons/bs';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { toast } from 'react-toastify';
import AIIcon from '../../../assets/icons/AIIcon.png';
import { NursingSummaryUploadFilesAPI, NursingSummaryTextInputAPI } from '../../../api/create';
import { useNursingSummaryWebSocket } from '../../../hooks/useNursingSummaryWebSocket';
import { SummaryResponse, PatientInfo } from '../../../types/nursing-summary.types';
import MarkdownBlock from '../../../components/mark-down';

interface UploadedFile {
    file: File;
    id: string;
}

const NotesUpload: React.FC = () => {
    const [textContent, setTextContent] = useState<string>('');
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'text' | 'file'>('text');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // API and WebSocket state
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [processing, setprocessing] = useState<string>('');
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
    const [generatedSummary, setGeneratedSummary] = useState<string | null>(null);
    const [summaryMetadata, setSummaryMetadata] = useState<any | null>(null);
    const [progressPercent, setProgressPercent] = useState<number>(0);
    const [progressMessage, setProgressMessage] = useState<string>('');

    // WebSocket hook
    const { messages,
        lastMessage,
        connectionStatus,
        connect,
        disconnect } = useNursingSummaryWebSocket();

    // Connection timeout tracking
    const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [wsConnectionAttempts, setWsConnectionAttempts] = useState<number>(0);
    const MAX_WS_RETRY_ATTEMPTS = 3;

    const acceptedFileTypes = ['.txt', '.pdf', '.doc', '.docx'];
    const maxFileSize = 10 * 1024 * 1024; // 10MB

    // Handle WebSocket messages
    useEffect(() => {
        if (lastMessage) {
            console.log('[WebSocket] Received message:', lastMessage);

            // Update progress message and percentage
            setProgressMessage(lastMessage.message);

            if (lastMessage.progress_percent !== undefined 
                && lastMessage.progress_percent !== null) {
                setProgressPercent(lastMessage.progress_percent);
                console.log(`[Progress] ${lastMessage.progress_percent}% 
                    - ${lastMessage.message}`);
            }

            // Handle different status types with appropriate feedback
            if (lastMessage.status === 'connected') {
                toast.info('Connected! Waiting for processing to start...', { autoClose: 2000 });
            }

            if (lastMessage.status === 'queued') {
                toast.info('Task queued for processing...', { autoClose: 2000 });
            }

            if (lastMessage.status === 'processing') {
                // Show intermediate progress notifications for key milestones
                const progress = lastMessage.progress_percent;
                if (progress === 20) {
                    toast.info('Analysis started...', { autoClose: 2000 });
                } else if (progress === 40) {
                    toast.info('Processing medical notes...', { autoClose: 2000 });
                } else if (progress === 70) {
                    toast.info('Summary generated!', { autoClose: 2000 });
                } else if (progress === 95) {
                    toast.info('Almost done! Finalizing...', { autoClose: 2000 });
                }
            }

            if (lastMessage.status === 'completed' && lastMessage.data) {
                // Clear any pending timeouts
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                if (lastMessage.data.summary) {
                    setGeneratedSummary(lastMessage.data.summary);
                    console.log('[Summary] Received summary:', lastMessage.data.summary.substring(0, 100) + '...');
                }
                if (lastMessage.data.metadata) {
                    setSummaryMetadata(lastMessage.data.metadata);
                    console.log('[Metadata]', lastMessage.data.metadata);
                }
                setIsProcessing(false);
                setWsConnectionAttempts(0);

                // Clear saved session from localStorage
                localStorage.removeItem('nursing_summary_active_session');

                toast.success('Summary generated successfully!', { autoClose: 3000 });
                disconnect();
            }

            if (lastMessage.status === 'error') {
                // Clear any pending timeouts
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current);
                    connectionTimeoutRef.current = null;
                }

                setIsProcessing(false);
                setWsConnectionAttempts(0);

                // Clear saved session from localStorage
                localStorage.removeItem('nursing_summary_active_session');

                console.error('[Error]', lastMessage.message);
                toast.error(lastMessage.message || 'An error occurred while processing', { autoClose: 5000 });
                disconnect();
            }
        }
    }, [lastMessage, disconnect]);

    // Monitor WebSocket connection status with timeout
    useEffect(() => {
        if (isProcessing) {
            if (connectionStatus === 'error' && wsConnectionAttempts < MAX_WS_RETRY_ATTEMPTS) {
                // Retry connection on error
                toast.warning(`Connection failed. Retrying... (${wsConnectionAttempts + 1}/${MAX_WS_RETRY_ATTEMPTS})`);
                setWsConnectionAttempts(prev => prev + 1);

                if (currentSessionId) {
                    setTimeout(() => {
                        connect(currentSessionId);
                    }, 2000);
                }
            } else if (connectionStatus === 'error' && wsConnectionAttempts >= MAX_WS_RETRY_ATTEMPTS) {
                // Max retries reached
                toast.error('Failed to establish connection after multiple attempts. Please try again.');
                setIsProcessing(false);
                setProgressMessage('Connection failed');
                setWsConnectionAttempts(0);
            } else if (connectionStatus === 'connected') {
                // Reset retry counter on successful connection
                setWsConnectionAttempts(0);
                setProgressPercent(10);

                // Set timeout for processing (5 minutes max)
                connectionTimeoutRef.current = setTimeout(() => {
                    toast.error('Processing timeout. The operation took too long.');
                    setIsProcessing(false);
                    setProgressMessage('Processing timeout');
                    disconnect();
                }, 5 * 60 * 1000); // 5 minutes
            }
        }

        return () => {
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
                connectionTimeoutRef.current = null;
            }
        };
    }, [connectionStatus, 
        isProcessing, 
        wsConnectionAttempts, 
        currentSessionId, 
        connect, disconnect]);

    // Warn user before leaving page during processing
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isProcessing) {
                e.preventDefault();
                e.returnValue = 'Processing is in progress. Are you sure you want to leave? Your progress will be lost.';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isProcessing]);

    // Check for active session on component mount (page refresh recovery)
    useEffect(() => {
        const checkActiveSession = () => {
            const savedSession = localStorage.getItem('nursing_summary_active_session');
            if (savedSession) {
                try {
                    const sessionData = JSON.parse(savedSession);
                    const { session_id, task_id, timestamp } = sessionData;

                    // Check if session is recent (within last 10 minutes)
                    const sessionAge = Date.now() - timestamp;
                    if (sessionAge < 10 * 60 * 1000) {
                        // Offer to reconnect
                        const shouldReconnect = window.confirm(
                            'You have an active processing session. Would you like to reconnect and check its status?'
                        );

                        if (shouldReconnect && session_id) {
                            setCurrentSessionId(session_id);
                            setCurrentTaskId(task_id || null);
                            setIsProcessing(true);
                            setProgressMessage('Reconnecting to session...');
                            toast.info('Attempting to reconnect to your previous session...');
                            connect(session_id);
                        } else {
                            // Clear old session
                            localStorage.removeItem('nursing_summary_active_session');
                        }
                    } else {
                        // Session too old, clear it
                        localStorage.removeItem('nursing_summary_active_session');
                    }
                } catch (error) {
                    console.error('Failed to parse saved session:', error);
                    localStorage.removeItem('nursing_summary_active_session');
                }
            }
        };

        checkActiveSession();
    }, [connect]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current);
            }
            disconnect();
        };
    }, [disconnect]);

    const getFileIcon = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return <BsFiletypePdf size={24} className="text-red-500" />;
            case 'doc':
            case 'docx':
                return <BsFiletypeDoc size={24} className="text-blue-500" />;
            case 'txt':
                return <BsFiletypeTxt size={24} className="text-gray-500" />;
            default:
                return <FiFile size={24} className="text-gray-400" />;
        }
    };

    const validateFile = (file: File): boolean => {
        // Check if file exists and has content
        if (!file || file.size === 0) {
            toast.error(`File "${file?.name || 'unknown'}" is empty or invalid.`);
            return false;
        }

        // Check file extension
        const extension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!extension || extension === '.') {
            toast.error(`File "${file.name}" has no extension.`);
            return false;
        }

        if (!acceptedFileTypes.includes(extension)) {
            toast.error(`Invalid file type "${extension}". Accepted: ${acceptedFileTypes.join(', ')}`);
            return false;
        }

        // Check file size
        if (file.size > maxFileSize) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
            toast.error(`File "${file.name}" (${sizeMB}MB) exceeds 10MB limit.`);
            return false;
        }

        // Check for suspicious file names
        if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
            toast.error(`File "${file.name}" has invalid characters in name.`);
            return false;
        }

        return true;
    };

    const handleFileSelect = (files: FileList | null) => {
        if (!files || files.length === 0) {
            toast.warning('No files selected.');
            return;
        }

        // Check total number of files
        const totalFilesAfterUpload = uploadedFiles.length + files.length;
        if (totalFilesAfterUpload > 10) {
            toast.error(`You can only upload a maximum of 10 files. Currently: ${uploadedFiles.length}`);
            return;
        }

        const validFiles: UploadedFile[] = [];
        const existingFileNames = new Set(uploadedFiles.map(f => f.file.name.toLowerCase()));
        let duplicateCount = 0;

        Array.from(files).forEach((file) => {
            // Check for duplicates
            if (existingFileNames.has(file.name.toLowerCase())) {
                duplicateCount++;
                return;
            }

            if (validateFile(file)) {
                validFiles.push({
                    file,
                    id: `${file.name}-${Date.now()}-${Math.random()}`,
                });
            }
        });

        if (duplicateCount > 0) {
            toast.warning(`${duplicateCount} duplicate file(s) skipped.`);
        }

        if (validFiles.length > 0) {
            setUploadedFiles((prev) => [...prev, ...validFiles]);
            toast.success(`${validFiles.length} file(s) added successfully!`);
        } else if (duplicateCount === 0) {
            toast.error('No valid files to upload.');
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const handleRemoveFile = (id: string) => {
        setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
        toast.info('File removed');
    };

    const handleSubmit = async (todoExtraction = false) => {
        // Prevent concurrent submissions
        if (isProcessing) {
            toast.warning('A request is already being processed. Please wait...');
            return;
        }

        // Validate input based on active tab
        if (activeTab === 'text') {
            if (!textContent.trim()) {
                toast.warning('Please enter some text before submitting.');
                return;
            }
            if (textContent.trim().length < 10) {
                toast.warning('Text content is too short. Please enter at least 10 characters.');
                return;
            }
            if (textContent.length > 500000) {
                toast.error('Text content is too large. Maximum 500,000 characters allowed.');
                return;
            }
        } else {
            if (uploadedFiles.length === 0) {
                toast.warning('Please upload at least one file before submitting.');
                return;
            }
        }

        // Create abort controller for request timeout
        const abortController = new AbortController();
        const requestTimeout = setTimeout(() => {
            abortController.abort();
        }, 30000); // 30 second timeout for initial API call

        try {
            setIsProcessing(true);
            setprocessing(todoExtraction ? 'todo' : 'summary');
            setProgressPercent(0);
            setProgressMessage('Initializing...');
            setGeneratedSummary(null);
            setSummaryMetadata(null);
            setWsConnectionAttempts(0);

            let response: SummaryResponse;

            if (activeTab === 'text') {
                // Submit text input
                toast.info(todoExtraction ? 'Submitting text for todo extraction...' : 'Submitting text for processing...');
                response = await Promise.race([
                    NursingSummaryTextInputAPI({
                        text_content: textContent,
                        todo_extraction: todoExtraction,
                    }),
                    new Promise<never>((_, reject) =>
                        setTimeout(() => reject(new Error('Request timeout')), 30000)
                    ),
                ]);
            } else {
                // Upload files
                toast.info('Uploading files...');

                // Build FormData
                const formData = new FormData();
                uploadedFiles.forEach((item) => {
                    formData.append('files', item.file);
                });
                formData.append('todo_extraction', String(todoExtraction));

                response = await Promise.race([
                    NursingSummaryUploadFilesAPI(formData),
                    new Promise<never>((_, reject) =>
                        setTimeout(() => reject(new Error('Upload timeout')), 60000)
                    ),
                ]);
            }

            clearTimeout(requestTimeout);

            // Validate response structure
            if (!response) {
                throw new Error('Empty response from server');
            }

            if (response.success && response.session_id) {
                setCurrentSessionId(response.session_id);
                setCurrentTaskId(response.task_id || null);

                // Save session info to localStorage for page refresh recovery
                localStorage.setItem('nursing_summary_active_session', JSON.stringify({
                    session_id: response.session_id,
                    task_id: response.task_id,
                    timestamp: Date.now(),
                    input_type: activeTab,
                }));

                // Show backend message to user
                if (response.message) {
                    toast.success(response.message);
                } else {
                    toast.success('Processing started! Connecting to live updates...');
                }

                // Connect to WebSocket for progress updates
                connect(response.session_id);

                // Note: Summary will come via WebSocket, not immediately in response
                // Backend uses Celery worker for async processing
                if (response.summary) {
                    // Fallback if somehow summary is immediately available
                    setGeneratedSummary(response.summary);
                    setSummaryMetadata(response.metadata);
                    setIsProcessing(false);
                    localStorage.removeItem('nursing_summary_active_session');
                }
            } else {
                throw new Error(response.error || 'Failed to process request');
            }
        } catch (error: any) {
            clearTimeout(requestTimeout);
            console.error('Submission error:', error);

            // Handle specific error types
            if (error.name === 'AbortError' || error.message === 'Request timeout' || error.message === 'Upload timeout') {
                toast.error('Request timeout. Please check your connection and try again.');
            } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
                toast.error('Network error. Please check your internet connection.');
            } else if (error.response?.status === 413) {
                toast.error('Files too large. Please reduce file sizes and try again.');
            } else if (error.response?.status === 400) {
                toast.error(error.response?.data?.error || 'Invalid request. Please check your input.');
            } else if (error.response?.status === 500) {
                toast.error('Server error. Please try again later or contact support.');
            } else {
                toast.error(error.message || 'Failed to submit. Please try again.');
            }

            setIsProcessing(false);
            setprocessing('');
            setProgressPercent(0);
            setProgressMessage('');
            disconnect();
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const waitingForProcessing = () => {
        // toast.info('Processing is in progress. Please wait.');
    };

    return (
        <div className="bg-gradient-to-br from-gray-50 via-teal-50 to-cyan-50 p-4 rounded-2xl mt-2">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-3">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-white p-2 rounded-xl shadow-lg border-2 border-teal-200">
                            <img src={AIIcon} alt="Notes" className="w-10 h-10" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Notes <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Upload</span>
                            </h1>
                            <p className="text-gray-600 text-xs">Upload your documents to extract and summarize medical notes</p>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Left Side - Upload Area */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                            {/* Tab Navigation */}
                            <div className="flex border-b border-gray-200 bg-gradient-to-r from-teal-50 to-cyan-50">
                                <button
                                    onClick={() => {
                                        if (isProcessing) {
                                            waitingForProcessing()
                                        } else {
                                            setActiveTab('text')
                                        }

                                    }}
                                    className={`flex-1 py-3 px-4 font-semibold text-sm transition-all duration-300 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''} ${activeTab === 'text'
                                            ? 'bg-white text-teal-700 border-b-2 border-teal-600'
                                            : 'text-gray-600 hover:bg-white/50'
                                        }`}
                                >
                                    <FiFileText className="inline-block mr-2" size={18} />
                                    Type or Paste Text
                                </button>
                                <button
                                    onClick={() => {
                                        if (isProcessing) {
                                            waitingForProcessing()
                                        } else {
                                            setActiveTab('file')
                                        }
                                    }}
                                    className={`flex-1 py-3 px-4 font-semibold text-sm transition-all duration-300 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''} ${activeTab === 'file'
                                            ? 'bg-white text-teal-700 border-b-2 border-teal-600'
                                            : 'text-gray-600 hover:bg-white/50'
                                        }`}
                                >
                                    <FiUpload className="inline-block mr-2" size={18} />
                                    Upload Files
                                </button>
                            </div>

                            {/* Content Area */}
                            <div className="p-4">
                                {activeTab === 'text' ? (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Enter Your Notes
                                            </label>
                                            <textarea
                                                value={textContent}
                                                onChange={(e) => {
                                                    if (isProcessing) {
                                                        waitingForProcessing()
                                                    } else {
                                                        setTextContent(e.target.value)
                                                    }
                                                }}
                                                placeholder="Type or paste your notes here..."
                                                className={`w-full h-64 p-4 border-2 border-gray-300 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all duration-200 resize-none text-gray-700 placeholder-gray-400 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                style={{ fontFamily: 'inherit' }}
                                            />
                                        </div>
                                        <div className="flex items-center justify-between text-sm text-gray-500">
                                            <span>{textContent.length} characters</span>
                                            <button
                                                onClick={() => {
                                                    if (isProcessing) {
                                                        waitingForProcessing()
                                                    } else {
                                                        setTextContent('')
                                                    }
                                                }}
                                                className={`px-4 py-1.5 rounded-lg font-semibold text-xs border border-red-200 transition-all duration-200 ${isProcessing || textContent.length === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700'}`}
                                            >
                                                Clear Text
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Drag & Drop Area */}
                                        <div
                                            onDragOver={(e) => {
                                                if (isProcessing) {
                                                    waitingForProcessing()
                                                } else {
                                                    handleDragOver(e)
                                                }
                                            }}
                                            onDragLeave={(e) => {
                                                if (isProcessing) {
                                                    waitingForProcessing()
                                                } else {
                                                    handleDragLeave(e)
                                                }
                                            }}
                                            onDrop={(e) => {
                                                if (isProcessing) {
                                                    waitingForProcessing()
                                                } else {
                                                    handleDrop(e)
                                                }
                                            }}
                                            className={`border-3 border-dashed rounded-xl p-6 text-center transition-all duration-300  ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isDragging
                                                    ? 'border-teal-500 bg-teal-50'
                                                    : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/30'
                                                }`}
                                            onClick={() => {
                                                if (isProcessing) {
                                                    waitingForProcessing()
                                                } else {
                                                    fileInputRef.current?.click()
                                                }
                                            }}
                                        >
                                            <AiOutlineCloudUpload
                                                size={48}
                                                className={`mx-auto mb-3 transition-colors duration-300 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''} ${isDragging ? 'text-teal-600' : 'text-gray-400'
                                                    }`}
                                            />
                                            <h3 className="text-base font-semibold text-gray-700 mb-2">
                                                Drop files here or click to browse
                                            </h3>
                                            <p className="text-xs text-gray-500 mb-3">
                                                Supports: TXT, PDF, DOC, DOCX (Max 10MB per file)
                                            </p>
                                            <button className={`bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-teal-700 hover:to-cyan-700 transition-all duration-200 shadow-lg ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                Select Files
                                            </button>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                accept={acceptedFileTypes.join(',')}
                                                onChange={(e) => {
                                                    if (isProcessing) {
                                                        waitingForProcessing()
                                                    } else {
                                                        handleFileSelect(e.target.files)
                                                    }
                                                }}
                                                className="hidden"
                                            />
                                        </div>

                                        {/* Uploaded Files List */}
                                        {uploadedFiles.length > 0 && (
                                            <div className="space-y-2">
                                                <h4 className="text-sm font-semibold text-gray-700">
                                                    Uploaded Files ({uploadedFiles.length})
                                                </h4>
                                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                                    {uploadedFiles.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            className="flex items-center justify-between 
                                                            p-3 bg-gray-50 rounded-lg border border-gray-200 
                                                            hover:bg-gray-100 transition-colors duration-200"
                                                        >
                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                {getFileIcon(item.file.name)}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium
                                                                     text-gray-700 truncate">
                                                                        {item.file.name}
                                                                    </p>
                                                                    <p
                                                                        className="text-xs
                                                                        text-gray-500"
                                                                    >
                                                                        {formatFileSize(
                                                                            item.file.size
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() =>{
                                                                    if (isProcessing) {
                                                                        waitingForProcessing()
                                                                    } else {
                                                                        handleRemoveFile(item.id)
                                                                    }
                                                                }}
                                                                className={`ml-2 p-1 text-red-500
                                                                hover:bg-red-50 rounded-full
                                                                transition-colors duration-200 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <FiX size={18} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Info & Actions */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Quick Info Card */}
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4">
                            <h3 className="text-base font-bold text-gray-900 mb-3">Quick Info</h3>
                            <div className="space-y-2 text-xs">
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5"></div>
                                    <p className="text-gray-600">
                                        Upload medical documents for
                                        automated analysis and summarization
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5"></div>
                                    <p className="text-gray-600">
                                        Supports text input and multiple file formats
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-1.5"></div>
                                    <p className="text-gray-600">
                                        Maximum file size: 10MB per file
                                    </p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5"></div>
                                    <p className="text-gray-600">
                                        Accepted formats: TXT, PDF, DOC, DOCX
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Status Card */}
                        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl shadow-xl border border-teal-200 p-4">
                            <h3 className="text-base font-bold text-gray-900 mb-3">Current Status</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-700 font-medium">Active Tab:</span>
                                    <span className="text-xs font-semibold text-teal-700 capitalize">
                                        {activeTab === 'text' ? 'Text Input' : 'File Upload'}
                                    </span>
                                </div>
                                {activeTab === 'text' ? (
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-700 font-medium">Characters:</span>
                                        <span className="text-xs font-semibold text-teal-700">
                                            {textContent.length}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-700 font-medium">Files:</span>
                                        <span className="text-xs font-semibold text-teal-700">
                                            {uploadedFiles.length}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-700 font-medium">Status:</span>
                                    <span
                                        className={`text-xs font-semibold ${(activeTab === 'text' && textContent.trim()) ||
                                                (activeTab === 'file' && uploadedFiles.length > 0)
                                                ? 'text-green-600'
                                                : 'text-orange-600'
                                            }`}
                                    >
                                        {(activeTab === 'text' && textContent.trim()) ||
                                            (activeTab === 'file' && uploadedFiles.length > 0)
                                            ? 'Ready'
                                            : 'Waiting'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    if (isProcessing) return;
                                    handleSubmit(false);
                                }}
                                disabled={isProcessing}
                                className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-xl transform ${isProcessing
                                        ? 'bg-[#0d948887] cursor-not-allowed'
                                        : 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white hover:from-teal-700 hover:to-cyan-700 hover:-translate-y-1'
                                    }`}
                            >
                                {isProcessing && processing === 'summary' ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <FiLoader className="animate-spin" size={20} />
                                        Processing...
                                    </span>
                                ) : (
                                    'Summarize'
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    if (isProcessing) return;
                                    handleSubmit(true);
                                }}
                                disabled={isProcessing}
                                className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-xl transform ${isProcessing
                                        ? 'bg-[#f973166e] cursor-not-allowed'
                                        : 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 hover:-translate-y-1'
                                    }`}
                            >
                                {isProcessing && processing === 'todo' ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <FiLoader className="animate-spin" size={20} />
                                        Processing...
                                    </span>
                                ) : (
                                    'To Do'
                                )}
                            </button>
                        </div>

                        {/* Clear All Button */}
                        <button
                            onClick={() => {
                                setTextContent('');
                                setUploadedFiles([]);
                                setGeneratedSummary(null);
                                setSummaryMetadata(null);
                                setProgressPercent(0);
                                setProgressMessage('');
                                setCurrentSessionId(null);
                                setCurrentTaskId(null);
                                disconnect();
                                toast.info('All content cleared');
                            }}
                            disabled={isProcessing}
                            className="w-full border-2 border-gray-300 text-gray-700 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                {/* Progress Tracking */}
                {isProcessing && (
                    <div className="mt-6 bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <FiLoader className="animate-spin text-teal-600" size={24} />
                            <h3 className="text-lg font-bold text-gray-900">Processing Your Request</h3>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-4">
                            <div className="flex justify-between text-sm text-gray-600 mb-2">
                                <span className="font-medium">{progressMessage || 'Processing...'}</span>
                                <span className="font-bold text-teal-600">{progressPercent}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                                <div
                                    className="bg-gradient-to-r from-teal-600 to-cyan-600 h-4 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                                    style={{ width: `${progressPercent}%` }}
                                >
                                    {progressPercent > 10 && (
                                        <span className="text-xs text-white font-bold">{progressPercent}%</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Progress Stages Indicator */}
                        <div className="mb-4 grid grid-cols-6 gap-2">
                            <div className={`text-center p-2 rounded-lg transition-all ${progressPercent >= 10 ? 'bg-teal-100 border-2 border-teal-500' : 'bg-gray-100 border border-gray-300'}`}>
                                <div className={`text-xs font-semibold ${progressPercent >= 10 ? 'text-teal-700' : 'text-gray-500'}`}>Start</div>
                                <div className="text-xs text-gray-600 mt-1">10%</div>
                            </div>
                            <div className={`text-center p-2 rounded-lg transition-all ${progressPercent >= 20 ? 'bg-teal-100 border-2 border-teal-500' : 'bg-gray-100 border border-gray-300'}`}>
                                <div className={`text-xs font-semibold ${progressPercent >= 20 ? 'text-teal-700' : 'text-gray-500'}`}>Analysis</div>
                                <div className="text-xs text-gray-600 mt-1">20%</div>
                            </div>
                            <div className={`text-center p-2 rounded-lg transition-all ${progressPercent >= 40 ? 'bg-teal-100 border-2 border-teal-500' : 'bg-gray-100 border border-gray-300'}`}>
                                <div className={`text-xs font-semibold ${progressPercent >= 40 ? 'text-teal-700' : 'text-gray-500'}`}>Processing</div>
                                <div className="text-xs text-gray-600 mt-1">40%</div>
                            </div>
                            <div className={`text-center p-2 rounded-lg transition-all ${progressPercent >= 70 ? 'bg-teal-100 border-2 border-teal-500' : 'bg-gray-100 border border-gray-300'}`}>
                                <div className={`text-xs font-semibold ${progressPercent >= 70 ? 'text-teal-700' : 'text-gray-500'}`}>Generated</div>
                                <div className="text-xs text-gray-600 mt-1">70%</div>
                            </div>
                            <div className={`text-center p-2 rounded-lg transition-all ${progressPercent >= 85 ? 'bg-teal-100 border-2 border-teal-500' : 'bg-gray-100 border border-gray-300'}`}>
                                <div className={`text-xs font-semibold ${progressPercent >= 85 ? 'text-teal-700' : 'text-gray-500'}`}>Saving</div>
                                <div className="text-xs text-gray-600 mt-1">85%</div>
                            </div>
                            <div className={`text-center p-2 rounded-lg transition-all ${progressPercent >= 100 ? 'bg-green-100 border-2 border-green-500' : 'bg-gray-100 border border-gray-300'}`}>
                                <div className={`text-xs font-semibold ${progressPercent >= 100 ? 'text-green-700' : 'text-gray-500'}`}>Complete</div>
                                <div className="text-xs text-gray-600 mt-1">100%</div>
                            </div>
                        </div>

                        {/* Connection Status */}
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500' :
                                        connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                                            connectionStatus === 'error' ? 'bg-red-500' :
                                                'bg-gray-400'
                                    }`}></span>
                                <span className="capitalize">
                                    {connectionStatus === 'connected' ? 'Processing Service Connected' :
                                        connectionStatus === 'connecting' ? 'Connecting to Processing Service...' :
                                            connectionStatus === 'error' ? 'Connection Error' :
                                                'Disconnected'}
                                </span>
                            </div>
                        </div>

                        {/* Progress Messages */}
                        {messages.length > 0 && (
                            <div className="mt-4 max-h-32 overflow-y-auto space-y-1">
                                {messages
                                    .filter(msg => {
                                        const msgLower = msg.message.toLowerCase();
                                        return !msgLower.includes('websocket connection established') &&
                                            !msgLower.includes('processing service connected') &&
                                            msg.status !== 'connected';
                                    })
                                    .slice(-5)
                                    .map((msg, idx) => (
                                        <div key={idx} className="text-xs text-gray-500 flex items-start gap-2">
                                            <span className="text-teal-600">•</span>
                                            <span>{msg.message}</span>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Summary Display */}
                {generatedSummary && !isProcessing && (
                    <div className="mt-6 bg-white rounded-2xl shadow-xl border border-green-200 p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <FiCheckCircle className="text-green-600" size={28} />
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">{processing === 'todo' ? 'To Do' : 'Summary'} Generated Successfully</h3>
                                {summaryMetadata && (
                                    <p className="text-sm text-gray-600">
                                        {summaryMetadata.summary_length && `${summaryMetadata.summary_length} characters`}
                                        {summaryMetadata.word_count && ` • ${summaryMetadata.word_count} words`}
                                        {summaryMetadata.files_count && ` • ${summaryMetadata.files_count} files processed`}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Summary Content */}
                        <div className="bg-gradient-to-br from-gray-50 to-teal-50 rounded-xl p-5 border border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                                Nursing Summary
                            </h4>
                            <div className="prose prose-sm max-w-none text-gray-800">
                                <MarkdownBlock mark_down_content={generatedSummary || ""} />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 mt-4">
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(generatedSummary);
                                    toast.success('Summary copied to clipboard!');
                                }}
                                className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:from-teal-700 hover:to-cyan-700 transition-all duration-200"
                            >
                                Copy {processing === 'todo' ? 'To Do' : 'Summary'}
                            </button>
                            <button
                                onClick={() => {
                                    setGeneratedSummary(null);
                                    setSummaryMetadata(null);
                                }}
                                className="px-6 border-2 border-gray-300 text-gray-700 py-2.5 rounded-lg font-semibold text-sm hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotesUpload;